import React from 'react';
import {WifiOff} from 'lucide-react';
import {cn, isDarkColor} from '@chekinapp/ui';
import {useFocusHeadingAndAnnounce} from 'hooks/useFocusHeadingAndAnnounce';
import {useBrandingTheme} from 'context/brandingTheme';
import {TrustFooter} from 'components/TrustFooter';
import {SPLASH_FADE_TRANSITION_CLASS} from 'components/SplashScreen/constants';
import {SplashLogoPlate} from 'components/SplashScreen/SplashLogoPlate';
import {DARK_INK_VARS, LIGHT_INK_VARS} from 'components/SplashScreen/splashInk';

type TerminalSceneProps = {
  title: string;
  subtitle: string;
  announce: string;
  children?: React.ReactNode;
};

function TerminalScene({title, subtitle, announce, children}: TerminalSceneProps) {
  const {data} = useBrandingTheme();
  const {headingRef, announcement} = useFocusHeadingAndAnnounce(announce);
  const [hasEntered, setHasEntered] = React.useState(false);

  const isDark = isDarkColor(data?.splash_screen_background_color);

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setHasEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const enterClassName = cn(
    SPLASH_FADE_TRANSITION_CLASS,
    hasEntered ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0',
  );

  return (
    <div
      className="relative flex min-h-[var(--app-dvh,100dvh)] w-full flex-col items-center justify-center overflow-hidden px-6 pb-[92px] pt-6"
      style={{
        background: 'var(--splash-bg, #f4f6f8)',
        ...(isDark ? DARK_INK_VARS : LIGHT_INK_VARS),
      }}
    >
      <SplashLogoPlate hostLogo={data?.splash_screen_logo} className="relative">
        <span
          aria-hidden="true"
          className="absolute bottom-[10px] right-[10px] grid size-[30px] place-items-center rounded-full border-[2.5px] border-[var(--sp-badge-ring)] bg-[var(--chekin-amber-700)] text-white"
        >
          <WifiOff size={14} />
        </span>
      </SplashLogoPlate>

      <div
        className={cn(
          'mt-5 flex max-w-[300px] flex-col items-center gap-[9px] text-center',
          enterClassName,
        )}
      >
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-[20px] font-semibold leading-[1.28] tracking-[-0.02em] text-[color:var(--sp-ink)] outline-none [text-wrap:balance]"
        >
          {title}
        </h1>
        <p className="text-[13.5px] font-medium leading-5 text-[color:var(--sp-ink-soft)]">
          {subtitle}
        </p>
      </div>

      {children && (
        <div
          className={cn(
            'mt-6 flex w-full max-w-[300px] flex-col items-center gap-2.5',
            enterClassName,
          )}
        >
          {children}
        </div>
      )}

      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>

      <TrustFooter isDark={isDark} />
    </div>
  );
}

export {TerminalScene};
